<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Side Navigation (Desktop Only) -->
    <SideNavbar currentRoute="profile" />

    <!-- Main Content Area with left margin for desktop -->
    <div class="md:ml-20">
      <!-- Navigation component -->
      <Navbar currentRoute="profile" />

    <div class="px-4 py-3 md:py-6 md:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <!-- Profile Header Section -->
        <div class="bg-gray-50 rounded-xl p-6 md:p-8 mb-6">
          <div class="flex flex-col items-center">
            <!-- Profile Photo and Followers (side by side on mobile) -->
            <div class="flex flex-row md:flex-col items-center justify-center md:justify-start w-full">
              <!-- Profile Photo -->
              <div class="relative mr-4 md:mr-0">
                <div class="profile-photo-container">
                  <div class="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gray-50 border-4 border-gray-50 overflow-hidden">
                    <img
                      :src="profilePhoto"
                      alt="Profile"
                      class="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div class="absolute -top-1 -right-1 bg-blue-500 rounded-full w-6 h-6 flex items-center justify-center text-white text-xs">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <!-- Vertical divider for mobile view -->
              <div class="h-16 w-px bg-gray-200 mx-8 md:hidden"></div>

              <!-- Friends (aligned to the right on mobile) -->
              <div
                class="flex flex-col items-center ml-4 md:ml-0 md:items-center md:mt-3 cursor-pointer"
                @click="showComingSoonModal = true"
              >
                <div class="flex items-center">
                  <div class="flex -space-x-2 mr-2">
                    <img src="https://m.media-amazon.com/images/I/61CG8tD+GFL._AC_UF1000,1000_QL80_.jpg" alt="Friend" class="w-6 h-6 rounded-full border border-gray-50" />
                    <img src="https://m.media-amazon.com/images/I/61CG8tD+GFL._AC_UF1000,1000_QL80_.jpg" alt="Friend" class="w-6 h-6 rounded-full border border-gray-50" />
                    <img src="https://m.media-amazon.com/images/I/61CG8tD+GFL._AC_UF1000,1000_QL80_.jpg" alt="Friend" class="w-6 h-6 rounded-full border border-gray-50" />
                  </div>
                  <span class="text-sm font-medium">1.5K</span>
                </div>
                <!-- Friends label - different placement for mobile and desktop -->
                <span class="text-xs text-gray-500 mt-1 md:hidden">Friends</span>
                <span class="hidden md:block text-sm text-gray-500 mt-2">Friends</span>
              </div>
            </div>

            <!-- User Name and Bio -->
            <h1 class="text-2xl font-bold mt-4 md:mt-4 nav-font text-left md:text-center w-full">{{ user.name || 'Christian Pulisic' }}</h1>
            <p class="text-gray-600 mt-2 text-left md:text-center max-w-md w-full">
              Hello, I'm {{ user.name ? user.name.split(' ')[0] : 'Chris' }} and I really like mountain climbing. 🏔️
            </p>

            <!-- Action Buttons -->
            <div class="flex space-x-4 mt-6 w-full justify-start md:justify-center px-4">
              <button
                @click="isEditProfileOpen = true"
                class="action-button flex items-center justify-center px-3 py-2.5 bg-gray-900 text-white rounded-full text-xs md:text-sm font-medium flex-1 max-w-[140px] whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Profile
              </button>
              <button
                @click="showComingSoonModal = true"
                class="action-button flex items-center justify-center px-3 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-full text-xs md:text-sm font-medium flex-1 max-w-[140px] whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Friends List
              </button>
            </div>
          </div>
        </div>

        <div class="space-y-4">
          <!-- Recent Visits -->
          <div class="bg-blue-50 rounded-xl p-6 flex items-center justify-between overflow-hidden relative border border-blue-100">
            <div class="z-10">
              <h3 class="text-lg font-bold text-gray-900">Recent Visits</h3>
              <p class="text-sm text-gray-600">0 of 10 Completed</p>
            </div>
            <!-- Tent icon on the right -->
            <div class="absolute right-0 bottom-0">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M40 10L10 60H70L40 10Z" fill="#4B83F2" />
                <path d="M40 30L25 60H55L40 30Z" fill="#2563EB" />
              </svg>
            </div>
          </div>

          <!-- Future Trips -->
          <div class="bg-green-50 rounded-xl p-6 flex items-center justify-between overflow-hidden relative border border-green-100">
            <div class="z-10">
              <h3 class="text-lg font-bold text-gray-900">Future Trips</h3>
              <p class="text-sm text-gray-600">0 of 10 Completed</p>
            </div>
            <!-- Trees icon on the right -->
            <div class="absolute right-0 bottom-0">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M30 60V40L20 50L30 30L15 45L30 20L10 40L30 10L5 30L25 5L0 20L20 0L40 20L60 0L80 20L55 5L75 30L50 10L70 40L50 20L65 45L50 30L60 50L50 40V60H30Z" fill="#22C55E" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

<!-- Footer -->
<footer class="pt-8 pb-6 text-center bg-gray-50">
  <p class="text-gray-500 text-sm font-medium">
    © {{ currentYear }} <span class="font-medium text-gray-700">Travley</span>. All rights reserved.
  </p>
</footer>


    <!-- Edit Profile Popup -->
    <EditProfilePopup
      :is-open="isEditProfileOpen"
      @close="isEditProfileOpen = false"
      :user="user"
      @update="handleProfileUpdate"
    />

    <!-- Friends List Popup -->
    <FriendsListPopup
      :is-open="isFriendsListOpen"
      @close="isFriendsListOpen = false"
      @removeFriend="handleRemoveFriend"
      :user="user"
    />

    <!-- Coming Soon Modal -->
    <ComingSoonModal
      :is-visible="showComingSoonModal"
      @close="showComingSoonModal = false"
    />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import EditProfilePopup from '@/components/EditProfilePopup.vue'
import FriendsListPopup from '@/components/FriendsListPopup.vue'
import Navbar from '@/components/Navbar.vue'
import SideNavbar from '@/components/SideNavbar.vue'
import ComingSoonModal from '@/components/ComingSoonModal.vue'

export default {
  name: 'ProfileView',
  components: {
    EditProfilePopup,
    FriendsListPopup,
    Navbar,
    SideNavbar,
    ComingSoonModal
  },
  setup() {
    const store = useStore()
    const isEditProfileOpen = ref(false)
    const isFriendsListOpen = ref(false)
    const showComingSoonModal = ref(false)
    const profilePhoto = ref('')

    // Get user from store
    const user = computed(() => store.getters.getUser)
    
    // Get Date
    const currentYear = new Date().getFullYear()


    // Set profile photo from user data
    onMounted(() => {
      if (!user.value) {
        store.dispatch('fetchUser')
      } else {
        profilePhoto.value = user.value.picture
      }
    })

    // Watch for user changes to update profile photo
    watch(() => user.value, (newUser) => {
      if (newUser) {
        profilePhoto.value = newUser.picture
      }
    }, { immediate: true })

    // Handle profile update from settings
    const handleProfileUpdate = async (updatedData) => {
      try {
        await store.dispatch('updateUserProfile', updatedData)

        // Update profile photo if it was changed
        if (updatedData.picture) {
          profilePhoto.value = updatedData.picture
        }
      } catch (error) {
        console.error('Error updating profile:', error)
      }
    }

    // Handle removing a friend
    const handleRemoveFriend = async (friend) => {
      try {
        // This is a temporary implementation until the API is ready
        // In a real app, you would dispatch a Vuex action to remove the friend
        alert(`Friend removal functionality will be implemented when the API is ready.\nWould remove: ${friend.name}`)
      } catch (error) {
        console.error('Error removing friend:', error)
      }
    }

    return {
      user,
      isEditProfileOpen,
      isFriendsListOpen,
      showComingSoonModal,
      profilePhoto,
      currentYear,
      handleProfileUpdate,
      handleRemoveFriend
    }
  }
}
</script>

<style scoped>
.nav-font {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

/* Add padding to account for fixed navigation */
.min-h-screen {
  padding-bottom: 60px;  /* Mobile bottom padding */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;     /* Firefox */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.min-h-screen::-webkit-scrollbar {
  display: none;
}

/* Dashed border around profile photo */
.profile-photo-container {
  position: relative;
  padding: 5px;
}

.profile-photo-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1.5px dashed #d1d5db;
  border-radius: 50%;
  z-index: 1;
}

/* Action buttons styling */
.action-button {
  transition: all 0.2s ease;
  min-width: 110px;
  display: flex;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.action-button:active {
  transform: scale(0.98);
}

@media (max-width: 360px) {
  .action-button {
    min-width: 100px;
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
}

@media (min-width: 768px) {
  .min-h-screen {
    padding-bottom: 0;    /* Remove bottom padding on desktop */
    padding-top: 60px;    /* Add top padding for desktop navbar */
  }

  /* Slightly larger dashed border for desktop */
  .profile-photo-container {
    padding: 7px;
  }
}
</style>
